import DefaultGrid from "../../../components/Common/DefaultGrid/DefaultGrid";
import { RiskThresholdColumnDefinitions } from "./ColumnDefs";
import { GetRowIdParams } from "ag-grid-community";
import {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  DefaultThreshold,
  CommodityThreshold,
} from "@/interfaces/RiskThresholdTypes";
import LoadingPage from "../../LoadingPage";
import Logger from "@/utils/Logger";
import { CommodityThresholdApi } from "@/api/RiskThreshold/CommodityThresholdApi";
import { DefaultThresholdApi } from "@/api/RiskThreshold/DefaultThresholdApi";

type RowData = CommodityThreshold | DefaultThreshold;

type GetRowStyleParamsLike = {
  node: {
    rowPinned?: "top" | "bottom" | null;
  };
  data?: RowData;
  api?: unknown;
};

const ERROR_MESSAGES = {
  UNABLE_TO_LOAD: "Unable to load threshold data",
  PROBLEM_RETRIEVING:
    "There was a problem retrieving the threshold information. Please try again later.",
} as const;

const UI_TEXT = {
  RETRY_BUTTON: "Retry",
  REFRESHING: "Refreshing thresholds...",
  LOADING_TITLE: "Loading thresholds...",
  ASTERISK_INDICATOR: "✱",
  ASTERISK_EXPLANATION:
    "Indicates that the commodities are using the default values.",
} as const;

const RiskThresholdTable = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [defaultThreshold, setDefaultThreshold] =
    useState<DefaultThreshold | null>(null);
  const [commodityThresholds, setCommodityThresholds] = useState<
    CommodityThreshold[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  /**
   * Fetch threshold data from APIs
   */
  const fetchThresholdData = useCallback(async (): Promise<void> => {
    try {
      Logger.info("Fetching threshold data", ["RiskThresholdTable"]);
      setError(false);

      const [defaultData, commoditiesData] = await Promise.all([
        DefaultThresholdApi.getActiveDefaults(),
        CommodityThresholdApi.getAllCommodityThresholds(),
      ]);

      setDefaultThreshold(defaultData);
      setCommodityThresholds(commoditiesData);

      Logger.info(
        `Successfully loaded ${commoditiesData.length} commodity thresholds`,
        ["RiskThresholdTable"],
      );
    } catch (err) {
      Logger.error("Failed to fetch threshold data", err, [
        "RiskThresholdTable",
      ]);
      setError(true);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  /**
   * Initial data load
   */
  useEffect(() => {
    void fetchThresholdData();
  }, [fetchThresholdData]);

  /**
   * Refresh handler for manual refresh or after mutations
   */
  const handleRefresh = useCallback(() => {
    Logger.info("Grid refresh triggered", ["RiskThresholdTable"]);
    setIsRefreshing(true);

    // Clear session cache to ensure fresh data
    sessionStorage.removeItem("activeThresholdDefaults");
    sessionStorage.removeItem("commodityThresholds");

    void fetchThresholdData();
  }, [fetchThresholdData]);

  const pinnedTopRowData = useMemo<DefaultThreshold[]>(() => {
    return defaultThreshold ? [defaultThreshold] : [];
  }, [defaultThreshold]);

  const getRowId = useCallback((params: GetRowIdParams<RowData>) => {
    const data = params.data;
    if (!data) return "";

    if ("thresholdDefaultId" in data && data.thresholdDefaultId != null) {
      return `default-${data.thresholdDefaultId}`;
    }

    if ("commodityThresholdId" in data && data.commodityThresholdId != null) {
      return `commodity-${data.commodityThresholdId}`;
    }

    return "";
  }, []);

  const getRowStyle = useCallback((params: GetRowStyleParamsLike) => {
    if (params.node?.rowPinned === "top") {
      return {
        background: "#D8EFFA",
        fontWeight: "500",
      };
    }
    return undefined;
  }, []);

  const showError = () => (
    <div className="flex flex-col items-center justify-center h-64">
      <p className="text-red-500 text-lg font-medium mb-2">
        {ERROR_MESSAGES.UNABLE_TO_LOAD}
      </p>
      <p className="text-gray-600">{ERROR_MESSAGES.PROBLEM_RETRIEVING}</p>
      <button
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={handleRefresh}
      >
        {UI_TEXT.RETRY_BUTTON}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-gray-50">
      <div className="mt-12 w-full px-6 md:px-12 lg:px-24 xl:px-32 2xl:px-48">
        <div className="border border-gray-300 rounded-sm shadow-sm bg-white relative">
          {isRefreshing && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
              <div className="flex flex-col items-center">
                <span className="icon-sync animate-spin text-blue text-4xl" />
                <p className="mt-2 text-gray-700">{UI_TEXT.REFRESHING}</p>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="min-w-[1250px]">
              {loading ? (
                <LoadingPage title={UI_TEXT.LOADING_TITLE} />
              ) : error ? (
                showError()
              ) : (
                <DefaultGrid
                  {...({
                    ref: gridRef,
                    rowData: commodityThresholds,
                    pinnedTopRowData: pinnedTopRowData,
                    columnDefs: RiskThresholdColumnDefinitions,
                    getRowId: getRowId,
                    getRowStyle: getRowStyle,
                    context: { onRefresh: handleRefresh },
                  } as unknown as ComponentProps<typeof DefaultGrid>)}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-1 text-[13px] text-gray-700">
          <span className="text-[#005DA4] font-bold text-lg leading-none">
            {UI_TEXT.ASTERISK_INDICATOR}
          </span>
          <span>{UI_TEXT.ASTERISK_EXPLANATION}</span>
        </div>
      </div>
    </div>
  );
};

export default RiskThresholdTable;
